import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Input, Icon, Dropdown, Table, Dimmer, Loader, DropdownProps, InputProps } from 'semantic-ui-react';
import dayjs, { Dayjs } from 'dayjs';
import API from '../../utils/network';
import Button from '../../components/elements/Button';
import DateInput from '../../components/elements/DateInput';
import { SemanticDatepickerProps } from 'react-semantic-ui-datepickers/dist/types';
import { stringIsNumber } from '../../utils/siteFunctions';
import { Transaction, TransactionDescription, TransactionType, Allocation } from '../../models/GeneralModels';

const AccountTransactionsPage = ({ acctNumber }: AccountTransactionsPageProps) => {
	/// Global State
	const pin = useSelector(state => state.ui.customer.pin);
	const secondaryColor = useSelector(state => state.ui.customer.secondaryColor);
	const client = useSelector(state => state.ui.client);

	/// Local State
	const [ transactionTypes, setTransactionTypes ] = useState<TransactionType[]>([]);
	// const [ transactionDescriptions, setTransactionDescriptions ] = useState<TransactionDescription[]>([]);
	const [ transactionTypeFilterSelection, setTransactionTypeFilterSelection ] = useState<number | undefined>(undefined);
	// const [ newTransactionDescriptionSelection, setNewTransactionDescriptionSelection ] = useState<number | undefined>(undefined);
	// const [ newTransactionAmount, setNewTransactionAmount ] = useState('');
	// const [ newTransactionNote, setNewTransactionNote ] = useState('');
	// const [ newTransactionDateError, setNewTransactionDateError ] = useState(false);
	// const [ newTransactionAmountError, setNewTransactionAmountError ] = useState(false);
	const [ fromDateFilterValue, setFromDateFilterValue ] = useState<Date | null>(null);
	const [ toDateFilterValue, setToDateFilterValue ] = useState<Date | null>(null);
	// const [ newTransactionDateValue, setNewTransactionDateValue ] = useState<Date | null>(null);
	const [ allocations, setAllocations ] = useState<Allocation[]>([]);
	const [ transactions, setTransactions ] = useState<Transaction[]>([]);
	const [ loadingTransactions, setLoadingTransactions ] = useState(true);

	/// Effects
	useEffect(() => {
		API.get('/settings/customer/transaction-types')
			.then(data => {
				setTransactionTypes(data.data.map(x => ({
					id: x.id,
					name: x.name,
					type: x.enumeration
				})));
				// if (data.data.length > 0)
				// 	setTransactionTypeFilterSelection(data.data.financialTransactionTypes[0].id);
			})
			.catch(error => console.error('Error', error));

		// API.get('account/transaction-descriptions')
		// 	.then(data => {
		// 		setTransactionDescriptions(data.data.financialTransactionDescriptions.map(x => ({
		// 			id: x.id,
		// 			description: x.description,
		// 			typeID: x.typeId
		// 		})));
		// 		if (data.data.financialTransactionDescriptions.length > 0)
		// 			setNewTransactionDescriptionSelection(data.data.financialTransactionDescriptions[0].id);
		// 	})
		// 	.catch(error => console.error('Error', error));

		API.get('/settings/customer/allocations')
			.then(data => {
				setAllocations(data.data.map(x => ({
					id: x.id,
					name: x.name
				})));
			})
			.catch(error => console.error('Error', error));
	}, [ acctNumber ]);

	useEffect(() => {
		setLoadingTransactions(true);
		API.post(`/client-portal/account/transactions/${encodeURIComponent(acctNumber)}`, {
			TransactionTypeId: transactionTypeFilterSelection,
			FromDate: fromDateFilterValue,
			ToDate: toDateFilterValue,
			IncludeDoesNotApplyToBalance: false
		})
			.then(data => {
				setTransactions(data.data.map(x => ({
					id: x.id,
					date: dayjs(x.date),
					description: x.transactionDescription,
					typeID: x.transactionTypeId,
					amount: x.amount,
					note: x.note,
					attorneyDisbursement: x.attorneyDisbursement,
					advancedDisbursement: x.advancedDisbursement,
					agencyDisbursement: x.agencyDisbursement,
					clientDisbursement: x.clientDisbursement,
					balance: x.balance,
					isVoid: x.isVoid,
					allocations: x.transactionAllocations.map(y => ({
						allocationID: y.id,
						amount: y.amount
					}))
				})));
				setLoadingTransactions(false);
			})
			.catch(error => console.error('Error', error));
	}, [acctNumber, transactionTypeFilterSelection, fromDateFilterValue, toDateFilterValue]);

	/// Events
	const handleTransactionTypeFilterChange = (e, { value }:DropdownProps) =>
		setTransactionTypeFilterSelection(value as number);

	// const handleNewTransactionDescriptionChange = (e, { value }:DropdownProps) =>
	// 	setNewTransactionDescriptionSelection(value as number);

	const handleFromDateFilterChange = (e, { value }:SemanticDatepickerProps) =>
		setFromDateFilterValue(value as Date | null);

	const handleToDateFilterChange = (e, { value }:SemanticDatepickerProps) =>
		setToDateFilterValue(value as Date | null);

	// const handleNewTransactionDateChange = (e, { value }:SemanticDatepickerProps) => {
	// 	setNewTransactionDateValue(value as Date | null);
	// 	setNewTransactionDateError(false);
	// };

	// const handleNewTransactionAmountChange = (e, { value }:InputProps) => {
	// 	setNewTransactionAmount(value as string);
	// 	setNewTransactionAmountError(false);
	// };

	// const handleNewTransactionNoteChange = (e, { value }:InputProps) =>
	// 	setNewTransactionNote(value as string);

	// const handleInsertTransactionClick = e => {
	// 	let dateIsValid = newTransactionDateValue !== null;
	// 	const amountIsValid = newTransactionAmount && stringIsNumber(newTransactionAmount);

	// 	if (dateIsValid) {
	// 		const newTransDate = dayjs(newTransactionDateValue as Date);
	// 		dateIsValid = newTransDate.isValid();

	// 		if (dateIsValid) {
	// 			// dev/account/{some account number}/insert-transaction
	// 			API.post(`account/${encodeURIComponent(acctNumber)}/insert-transaction`, ({
	// 				date: newTransactionDateValue,
	// 				description: newTransactionDescriptionSelection,
	// 				amount: parseFloat(newTransactionAmount),
	// 				note: newTransactionNote ? newTransactionNote : null
	// 			}))
	// 				.then(data => {
	// 					// TODO: respond to the data that is returned
	// 					setTransactions(prevState => prevState.concat([{
	// 						id: prevState.length + 1,
	// 						date: newTransDate,
	// 						description: transactionDescriptions.find(x => x.id === newTransactionDescriptionSelection)?.description || '',
	// 						amount: parseFloat(newTransactionAmount),
	// 						note: newTransactionNote,
	// 						attorneyDisbursement: 0,
	// 						advancedDisbursement: 0,
	// 						agencyDisbursement: 0,
	// 						clientDisbursement: 0,
	// 						balance: 0,
	// 						allocations: allocations.map(x => ({
	// 							allocationID: x.id,
	// 							amount: 0
	// 						}))
	// 					}]));
	// 				})
	// 				.catch(error => console.error('Error', error));
	// 		}
	// 	}

	// 	if (!dateIsValid)
	// 		setNewTransactionDateError(true);

	// 	if (!amountIsValid)
	// 		setNewTransactionAmountError(true);
	// };

	/// Render
	return (
		<>
			<div style={{
				display: 'grid',
				gridAutoFlow: 'column',
				alignItems: 'center',
				gap: '1em',
				justifyContent: 'start'
			}}>
				<Dropdown
					selection
					options={[ { key: -1, text: '[All Transaction Types]', value: undefined }, ...transactionTypes.map((x, index) => ({key: index, text: x.name, value: x.id})) ]}
					loading={transactionTypes.length === 0}
					value={transactionTypeFilterSelection}
					text={transactionTypeFilterSelection === undefined ? '[All Transaction Types]' : undefined}
					onChange={handleTransactionTypeFilterChange}
				/>
				From
				<DateInput
					placeholder="From Date..."
					value={fromDateFilterValue}
					onChange={handleFromDateFilterChange}
				/>
				To
				<DateInput
					placeholder="To Date..."
					value={toDateFilterValue}
					onChange={handleToDateFilterChange}
				/>
			</div>
			{/* <div style={{
				display: 'grid',
				gridAutoFlow: 'column',
				alignItems: 'center',
				gap: '1em',
				justifyContent: 'start',
				marginTop: '1em',
			}}>
				<DateInput
					placeholder="Date..."
					value={newTransactionDateValue}
					onChange={handleNewTransactionDateChange}
					error={newTransactionDateError}
				/>
				<Dropdown
					selection
					options={transactionDescriptions.map(x => ({key: x.id, text: x.description, value: x.id}))}
					loading={transactionDescriptions.length === 0}
					value={newTransactionDescriptionSelection}
					onChange={handleNewTransactionDescriptionChange}
				/>
				<Input
					placeholder="Amount..."
					value={newTransactionAmount}
					onChange={handleNewTransactionAmountChange}
					error={newTransactionAmountError}
				/>
				<Input
					placeholder="Transaction Note..."
					value={newTransactionNote}
					onChange={handleNewTransactionNoteChange}
				/>
				<Button secondary onClick={handleInsertTransactionClick}>Insert Transaction</Button>
			</div> */}

			{/* Transaction List */}
			{ loadingTransactions ? (
				<div
					style={{
						borderTop: `2px solid ${secondaryColor}`,
						position: 'relative',
						marginTop: '1em',
						height: '10em'
					}}
				>
					<Dimmer active inverted style={{ zIndex: 'unset' }}>
						<Loader size='massive'>Loading</Loader>
					</Dimmer>
				</div>
			) : (
				<div
					style={{
						borderTop: `2px solid ${secondaryColor}`,
						marginTop: '1em'
					}}
				>
					<Table celled striped>
						<Table.Header>
							<Table.Row>
								<Table.HeaderCell>Date</Table.HeaderCell>
								<Table.HeaderCell>Description</Table.HeaderCell>
								{/* <Table.HeaderCell>Note</Table.HeaderCell> */}
								<Table.HeaderCell>Amount</Table.HeaderCell>
								{client.showFieldsSettings.AllocationsAndDisbursements && allocations.map((x, index) => <Table.HeaderCell key={index}>{x.name}</Table.HeaderCell>)}
								<Table.HeaderCell>Balance</Table.HeaderCell>
							</Table.Row>
						</Table.Header>
						<Table.Body>
							{transactions
								.map(x =>
									<Table.Row
										key={x.id}
										style={x.isVoid ? { color: '#909090' } : {}}
									>
										<Table.Cell>{x.date.format('M/D/YYYY')}</Table.Cell>
										<Table.Cell>{x.description}{x.isVoid && ' (VOID)'}</Table.Cell>
										{/* <Table.Cell>{x.note}</Table.Cell> */}
										<Table.Cell>${x.amount.toFixed(2)}</Table.Cell>
										{client.showFieldsSettings.AllocationsAndDisbursements && allocations.map((y, index) => <Table.Cell key={index}>{['P', 'CP', 'NP'].includes(transactionTypes.find(z => z.id === x.typeID)?.type as string) && `${x.allocations.find(z => z.allocationID === y.id)?.amount.toFixed(2)}`}</Table.Cell>)}
										<Table.Cell>${x.balance.toFixed(2)}</Table.Cell>
									</Table.Row>
								)
							}
						</Table.Body>
					</Table>
				</div>
			)}
		</>
	);
};

interface AccountTransactionsPageProps {
	acctNumber: string
}

export default AccountTransactionsPage;